import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Wobbli } from 'wobbli/react';
import { STATE, STATES } from 'wobbli';
import { HOLIDAY, HOLIDAYS, REGION_CODES, REGIONS, holidayOn, today, upcoming } from 'wobbli/holidays';
import { codeFor, settingsToProps } from 'wobbli/formats';
import { CodeBox, Install } from '../components/ui';
import { Footer } from '../components/Layout';
import { lookProps, useSettings } from '../lib/settings';
import { usePm } from '../lib/pm';
import { PACKAGE_NAME, BLOBATAR_URL } from '../config';
import { costumeName, FAMILIES, pick, variantName } from '../lib/data';
import { fmtDay, inDays } from '../lib/dates';

// the hero's caption: "It <verb>"
const ALL_COMBOS = () => STATES.flatMap(s => s.variants.map(v => [s.key, v.key] as [string, string]));
const ALL_COS = HOLIDAYS.flatMap(h => h.costumes.map(c => [h.key, c.key] as [string, string]));
const FEAT_COS: [string, string][] = [['halloween', 'hw-witch'], ['muertos', 'dm-marigold'], ['christmas', 'xm-santa'], ['lny', 'ln-lion'], ['diwali', 'dw-diyas'], ['carnival', 'cv-feathers'], ['patrick', 'sp-hat'],
  ['lucia', 'lu-crown'], ['july4', 'jf-hat'], ['shogatsu', 'sj-shishi'], ['holi', 'ho-splash'], ['canadaday', 'cd-facepaint'], ['valentines', 'va-boppers'], ['karneval', 'ka-jester']];
const HOW = ['busy', 'happy', 'sad', 'curious', 'search', 'love', 'greet', 'gotit'];
const HCARDS: [string, 'dk' | 'lv' | 'lt'][] = [['halloween', 'dk'], ['christmas', 'lv'], ['diwali', 'dk'], ['muertos', 'lt']];
const NAMES = ['atlas', 'nova', 'pip', 'juniper', 'orbit', 'miso'];
const REP: Record<string, string> = { chat: 'busy', feelings: 'love', data: 'download', work: 'build', talk: 'greet', status: 'error' };
// the hero's face toggles: turning one on jumps to a state that shows it off
const SHOWS = { mouth: ['happy', 'surprise', 'anger', 'excitement'], hands: ['greet', 'love', 'gotit', 'happy', 'surprise'] };

// visitors from the Product Hunt listing (?ref=producthunt): the hero waves and a banner points at the name box,
// until they dismiss it for the session
const PH_KEY = 'wobbli:ph-hello';
function usePhHello() {
  const [q] = useSearchParams();
  // trimmed: the listing's link arrives as ?ref=producthunt%20
  const isPh = (v: string | null) => v?.trim().toLowerCase() === 'producthunt';
  const from = isPh(q.get('ref')) || isPh(q.get('utm_source'));
  const [open, setOpen] = useState(() => {
    if (!from) return false;
    try { return sessionStorage.getItem(PH_KEY) !== 'off'; } catch { return true; }
  });
  const close = () => {
    setOpen(false);
    try { sessionStorage.setItem(PH_KEY, 'off'); } catch { /* the banner just comes back next visit */ }
  };
  return [from, open, close] as const;
}

function useHero(speed: number, first?: [string, string]) {
  const deck = useRef<[string, string][]>([]);
  const [h, setH] = useState<{ st: string; v: string; cos: [string, string] | null }>({ st: 'idle', v: 'idle', cos: null });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const step = (force?: [string, string]) => {
    setH(prev => {
      let next = force;
      if (!next) {
        if (!deck.current.length) {
          const d = ALL_COMBOS();
          for (let i = d.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [d[i], d[j]] = [d[j], d[i]]; }
          if (d[d.length - 1][0] === prev.st) d.unshift(d.pop()!);
          deck.current = d;
        }
        next = deck.current.pop()!;
      }
      const [st, v] = next;
      const dur = st === 'happy' ? Math.max(2600, (STATE.happy.variants.find(x => x.key === v)?.ms || 1800) + 700) : st === 'idle' ? 2600 : 3400;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => step(), dur / speed);
      return { st, v, cos: !force && Math.random() < 0.4 ? pick(ALL_COS) : null };
    });
  };
  useEffect(() => { step(first); return () => { if (timer.current) clearTimeout(timer.current); }; }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [h, step] as const;
}

export default function Home() {
  const { s, set } = useSettings();
  const nav = useNavigate();
  const pm = usePm();
  const look = lookProps(s);
  const [fromPh, phOpen, phClose] = usePhHello();
  const [hero, heroStep] = useHero(s.speed, fromPh ? ['greet', 'grwave'] : undefined);
  const [face, setFace] = useState({ mouth: true, hands: true });
  const [code, setCode] = useState<'react' | 'events' | 'json'>('react');
  // rotating features, every 3.6 s
  const [tick, setTick] = useState(0);
  const feat = useMemo(() => ({
    f1: pick(STATE.busy.variants).key, f2: pick(STATE.happy.variants).key, f3: FEAT_COS[tick % FEAT_COS.length],
    how: (() => { const st = pick(HOW); return [st, pick(STATE[st].variants).key] as [string, string]; })()
  }), [tick]);
  useEffect(() => { const t = setInterval(() => setTick(n => n + 1), 3600); return () => clearInterval(t); }, []);
  const famVariant = useMemo(() => Object.fromEntries(FAMILIES.map(f => { const st = STATE[REP[f.key]] ?? f.states[0]; return [f.key, [st.key, pick(st.variants).key]]; })), []);

  // try a name: a preview that does not touch the studio look until you use it
  const [tryName, setTryName] = useState('');
  const [tryWave, setTryWave] = useState(false);
  const waveT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setTry = (n: string) => {
    setTryName(Array.from(n.trim()).slice(0, 40).join(''));
    setTryWave(true);
    if (waveT.current) clearTimeout(waveT.current);
    waveT.current = setTimeout(() => setTryWave(false), 1800 / s.speed);
  };
  const pinned = Object.keys(s.pins).length + (s.shape ? 1 : 0) + (s.colorMode !== 'auto' ? 1 : 0) + (s.eyeAuto ? 0 : 1) +
    (['hair', 'hairColor', 'beard', 'brows', 'clothes', 'clothesColor', 'glasses'] as const).filter(k => s[k] != null).length;
  const inUse = tryName === s.name && !pinned && s.dress === 'name';
  const useTry = () => {
    if (!tryName) return;
    set({ name: tryName, pins: {}, shape: null, colorMode: 'auto', eyeAuto: true, dress: 'name', hair: null, hairColor: null, beard: null, brows: null, clothes: null, clothesColor: null, glasses: null });
    nav('/studio');
  };
  const goTry = () => {
    const el = document.getElementById('tryName');
    el?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
    el?.focus({ preventScroll: true });
  };
  const own = (name: string) => ({ name, styleFromName: true, mouth: s.mouth, hands: s.hands, motion: s.motion, speed: s.speed, intensity: s.intensity });

  const d = today();
  const regionsOrdered = [s.region, ...REGION_CODES.filter(r => r !== s.region)];
  // Regions that share a holiday each wear a different costume: the first shows the day's costume, the next ones
  // the costumes after it (a holiday has five, so a sixth region repeats). Dated costumes (bank holidays) stay.
  const regionRows = (() => {
    const seen: Record<string, number> = {};
    return regionsOrdered.map(r => {
      const a = holidayOn(r, d, s.lead);
      if (!a || a.occurrence.costume) return { r, a };
      const list = HOLIDAY[a.key].costumes, n = seen[a.key] = (seen[a.key] ?? -1) + 1;
      const start = list.findIndex(c => c.key === a.costume);
      return { r, a: { ...a, costume: list[(start + n) % list.length].key } };
    });
  })();
  const props = settingsToProps(s);
  const heroCos = hero.cos ? { holiday: hero.cos[0], costume: hero.cos[1] } : {};
  const ways = STATES.reduce((n, x) => n + (x.key === 'idle' ? 0 : x.variants.length), 0);

  return (
    <div className="view" id="v-home">
      <section className="wrap hero" aria-labelledby="heroTitle">
        {phOpen ? (
          <aside className="ph-hello" aria-label="Welcome">
            <i aria-hidden="true" />
            <p>Hi, Product Hunt. Type your name and meet the avatar it gives you.</p>
            <button type="button" className="ph-go" onClick={goTry}>Try your name →</button>
            <button type="button" className="icon-btn ph-x" aria-label="Dismiss" onClick={phClose}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
          </aside>
        ) : null}
        <div className="hero-top">
          <div className="hero-l">
            <h1 id="heroTitle">An avatar that acts out what your agent is doing</h1>
            <div className="row"><Link className="btn btn-ink" to="/studio">Open the studio</Link><Link className="btn" to="/states">See all states</Link></div>
          </div>
          <div className="hero-r">
            <p className="lede">It reads while an answer generates, celebrates when one lands, slumps when a source fails and tilts its head when a question is unclear.</p>
            <p className="lede">Shape it, colour it, dress it for the holidays and drop it into your chat.</p>
            <Install />
          </div>
        </div>
        <div className="show dk-panel dk-stage" data-theme="dark">
          <div className="show-l">
            <p className="eyebrow">Live state</p>
            <div className="plate">
              <Wobbli {...look} {...heroCos} controlled state={hero.st} variant={hero.v} mouth={face.mouth} hands={face.hands} size="100%" />
            </div>
            <p className="it-line" aria-live="polite"><span className="it">It</span> <span>{STATE[hero.st].verb}</span></p>
            <p className="show-meta"><span>{STATE[hero.st].name}</span><span aria-hidden="true">·</span><span>{variantName(hero.st, hero.v)}</span>
              {hero.cos ? <span className="pill">{HOLIDAY[hero.cos[0]].label + ' · ' + costumeName(hero.cos[0], hero.cos[1])}</span> : null}</p>
            <div className="hero-face" role="group" aria-label="Try the face options">
              {(['mouth', 'hands'] as const).map(k => (
                <button key={k} type="button" className="tgl" aria-pressed={face[k]} onClick={() => {
                  const on = !face[k];
                  setFace({ ...face, [k]: on });
                  if (on) { const list = SHOWS[k].filter(x => x !== hero.st); const st = pick(list); heroStep([st, pick(STATE[st].variants).key]); }
                }}><i aria-hidden="true" />{k === 'mouth' ? 'Mouth' : 'Hands'}</button>
              ))}
            </div>
          </div>
          <figure className="win" aria-label="The same avatar in a chat">
            <div className="win-head">
              <div className="mini-stage"><Wobbli {...look} {...heroCos} controlled state={hero.st} variant={hero.v} mouth={face.mouth} hands={face.hands} size={40} /></div>
              <div><b>Assistant</b><small>Avatar in the chat header</small></div>
              <span className="win-pill"><i />{hero.st + (hero.st === 'idle' ? '' : ' · ' + variantName(hero.st, hero.v).toLowerCase())}</span>
            </div>
            <div className="win-body">
              <div className="win-user">Summarise last month for my team</div>
              <div className="win-ai"><div className="mini-stage"><Wobbli {...look} {...heroCos} controlled state={hero.st} variant={hero.v} mouth={face.mouth} hands={face.hands} size={34} /></div><div className="skel"><span /><span /><span /></div></div>
            </div>
            <p className="win-flow"><span><b>send</b> → busy</span><span>· <b>answer lands</b> → happy, once</span><span>· <b>error</b> → sad</span><span>· <b>unclear</b> → curious</span></p>
            <div className="win-foot"><Link to="/studio">Play a turn in the studio →</Link></div>
          </figure>
        </div>
      </section>

      <section className="wrap nums" aria-label="In numbers">
        <p className="eyebrow">Built on blobatar 2.7.0</p>
        <div className="nums-row">
          <div><b>{STATES.length}</b><span>states</span></div>
          <div><b>{ways}</b><span>ways to act them</span></div>
          <div><b>{HOLIDAYS.length}</b><span>holidays</span></div>
          <div><b>5</b><span>costumes each</span></div>
          <div><b>{Object.keys(REGIONS).length}</b><span>regions</span></div>
        </div>
      </section>

      <section className="wrap section" aria-labelledby="howTitle">
        <div className="center-head">
          <p className="eyebrow">How it works</p>
          <h2 id="howTitle">Your app sets the state. The avatar picks how to act it.</h2>
          <p>Idle, busy, happy, sad and curious cover a chat turn. The rest cover moving data, working, waiting, talking and system status. Every state has five ways to act it, picked at random and never the same twice running.</p>
        </div>
        <div className="how">
          <div className="how-col">
            <p className="eyebrow">Events from your app</p>
            <div className="ev-card"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 12l16-8-6 16-2.5-6.5z" /></svg><div><b>Message sent</b><span>plays busy</span></div></div>
            <div className="ev-card"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg><div><b>Answer lands</b><span>plays happy, once</span></div></div>
            <div className="ev-card"><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16.5v.5" /></svg><div><b>Source fails</b><span>plays sad</span></div></div>
            <div className="ev-card"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9.2 9.2a3 3 0 1 1 3.8 2.9c-.6.2-1 .7-1 1.4v.5M12 17v.5" /></svg><div><b>Question unclear</b><span>plays curious</span></div></div>
          </div>
          <div className="how-core dk-panel dk-stage" data-theme="dark">
            <span className="core-tab">&lt;Wobbli /&gt;</span>
            <ol className="core-steps">
              <li><span aria-hidden="true">1</span>The event map picks the state</li>
              <li><span aria-hidden="true">2</span>It picks one of five ways to act it</li>
              <li><span aria-hidden="true">3</span>A holiday costume rides on top</li>
            </ol>
            <div className="plate"><Wobbli {...look} controlled state={feat.how[0]} variant={feat.how[1]} size="100%" /></div>
            <p className="core-note">{feat.how[0] + ' · ' + variantName(feat.how[0], feat.how[1]).toLowerCase()}</p>
          </div>
          <div className="how-col">
            <p className="eyebrow">What people see</p>
            <div className="see-card"><div className="mini-stage"><Wobbli {...look} controlled state="busy" variant={feat.f1} size={60} /></div><div><span className="see-tag t2">Busy</span><b>{variantName('busy', feat.f1)}</b><small>While an answer generates</small></div></div>
            <div className="see-card"><div className="mini-stage"><Wobbli {...look} controlled state="happy" variant={feat.f2} size={60} /></div><div><span className="see-tag">Happy</span><b>{variantName('happy', feat.f2)}</b><small>When an answer lands</small></div></div>
            <div className="see-card"><div className="mini-stage"><Wobbli {...look} controlled state="idle" holiday={feat.f3[0]} costume={feat.f3[1]} size={60} /></div><div><span className="see-tag t3">Costume</span><b>{HOLIDAY[feat.f3[0]].label + ' · ' + costumeName(feat.f3[0], feat.f3[1]).toLowerCase()}</b><small>Switches on by date</small></div></div>
          </div>
        </div>
      </section>

      <section className="wrap section" aria-labelledby="qsTitle">
        <div className="split">
          <div className="split-l">
            <p className="eyebrow">Quick start</p>
            <h2 id="qsTitle">Drop it into your chat. <span className="soft">Three steps, one component.</span></h2>
            <ol className="steps">
              <li><span><b>Install the package</b> with npm, pnpm, bun or the shadcn registry.</span></li>
              <li><span><b>Add the component</b> where your assistant’s avatar goes. Every prop is optional except the name.</span></li>
              <li><span><b>Send events</b> as your agent works, and the event map picks the state.</span></li>
            </ol>
            <Link className="arrow" to="/studio">Tune every trait in the studio →</Link>
            <Link className="arrow" to="/recipes">Recipes for the Vercel AI SDK and Claude Agent SDK →</Link>
          </div>
          <div className="split-r">
            <CodeBox id="homePre" label="Example code" tall tabs={[['react', 'Component'], ['events', 'Events'], ['json', 'JSON']]} value={code} onTab={setCode} code={codeFor(code, props, pm, PACKAGE_NAME)} />
          </div>
        </div>
        <div className="cols4">
          <div><b>npm, pnpm, bun or shadcn</b><p>Install it from the registry you already use.</p></div>
          <div><b>One component</b><p>Put it where your assistant’s avatar goes. Every prop is optional except the name.</p></div>
          <div><b>An event map</b><p>Send events as your agent works, and the map picks the state.</p></div>
          <div><b>A share link</b><p>Install &amp; share in the studio gives you the config and a link that opens your look.</p></div>
        </div>
      </section>

      <section className="band" aria-label="The one rule">
        <p className="eyebrow">One rule</p>
        <p className="band-quote">“The colour is part of the look. States never change it.”</p>
        <Link className="arrow" to="/studio">Pick a colour in the studio →</Link>
      </section>

      <section className="wrap section" aria-labelledby="libTitle">
        <div className="center-head"><p className="eyebrow">Library</p><h2 id="libTitle">Six families of states</h2></div>
        <div className="lib">
          <div className="lib-l">
            <h3>{STATES.length} states, five ways to act each.</h3>
            <p>Each state picks one of its five ways to act at random and never repeats twice running. Pin one when you want it fixed.</p>
            <ul className="ticks">
              {['An optional mouth for every feeling', 'Optional hands that gesture with each state', 'The body colour never changes'].map(t => (
                <li key={t}><svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M7.5 12.5l3 3 6-6.5" /></svg>{t}</li>
              ))}
            </ul>
            <Link className="arrow" to="/states">See all {STATES.length} states →</Link>
          </div>
          <div className="fams">
            {FAMILIES.map(f => (
              <Link key={f.key} className="card fam" to={'/states?family=' + f.key}>
                <div className="mini-stage"><Wobbli {...look} controlled state={famVariant[f.key][0]} variant={famVariant[f.key][1]} size={76} /></div>
                <b>{f.name}</b>
                <small>{f.states.slice(0, 4).map(x => x.name).join(', ') + (f.states.length > 4 ? ' and ' + (f.states.length - 4) + ' more' : '')}</small>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="wrap section" aria-labelledby="holTitle">
        <div className="center-head"><p className="eyebrow">By date</p><h2 id="holTitle">Dressed for the day</h2><p>Turn holidays on and costumes switch on by date in {Object.keys(REGIONS).length} regions, from Diwali to Día de Muertos, and stay on through every state. Hats step aside for states that wear one.</p></div>
        <div className="hols">
          {HCARDS.map(([key, tone], i) => {
            const h = HOLIDAY[key], k = (tick + i) % h.costumes.length, c = h.costumes[k];
            return (
              <article className="hol-card" key={key}>
                <div className={'plate' + (tone === 'dk' ? ' dk-panel dk-stage' : tone === 'lv' ? ' lv' : '')} data-theme={tone === 'dk' ? 'dark' : undefined}>
                  <Wobbli {...look} controlled state="idle" holiday={key} costume={c.key} size="100%" />
                </div>
                <div><h3>{h.label}</h3><p>{c.name + ' · ' + (k + 1) + ' of ' + h.costumes.length}</p></div>
              </article>
            );
          })}
        </div>
        <h3 className="today-h">In every region today</h3>
        <p className="today-p">What each region’s avatar is wearing on {fmtDay(d)}, picked from the calendar{s.lead ? ' and shown up to ' + (s.lead === 7 ? 'a week' : s.lead + ' days') + ' early.' : '.'}</p>
        <div className="regions">
          {regionRows.map(({ r, a }) => {
            const nx = upcoming(r, d, 1)[0];
            return (
              <article className="card region" key={r}>
                <div className="mini-stage">{a ? <Wobbli {...look} controlled state="idle" holiday={a.key} costume={a.costume} size={64} /> : <Wobbli {...look} controlled state="idle" size={64} />}</div>
                <div>
                  <b>{REGIONS[r].name}</b>
                  <p>{a ? HOLIDAY[a.key].label + ' · ' + costumeName(a.key, a.costume) : 'No costume today'}</p>
                  <p className="when">{a ? (a.on ? (a.occurrence.start === a.occurrence.end ? 'Today' : 'Until ' + fmtDay(a.occurrence.end)) : 'Early: ' + fmtDay(a.occurrence.start) + ', ' + inDays(a.occurrence.start - d)) : nx ? 'Next: ' + HOLIDAY[nx.key].label + ', ' + fmtDay(nx.main) : ''}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="wrap section" aria-labelledby="namesTitle">
        <div className="sec-head">
          <div><p className="eyebrow">Seeded by name</p><h2 id="namesTitle">One name, one avatar</h2><p>Each name gives its own avatar, the same one in every app. Pin a few settings in the studio and a team of agents can share a style and still be told apart.</p></div>
          <Link className="arrow" to="/studio">Customize in the studio →</Link>
        </div>
        <div className="name-try">
          <div className="plate">
            {tryName
              ? <Wobbli {...own(tryName)} controlled state={tryWave ? 'greet' : 'idle'} variant={tryWave ? 'grwave' : 'idle'} size="100%" />
              : <Wobbli {...look} controlled state={tryWave ? 'greet' : 'idle'} variant={tryWave ? 'grwave' : 'idle'} size="100%" />}
          </div>
          <div className="name-try-r">
            <label className="field-label" htmlFor="tryName">Try a name</label>
            <div className="name-row">
              <input className="input name-in" id="tryName" type="text" autoComplete="off" spellCheck={false} maxLength={40} placeholder="Type any name" value={tryName}
                onChange={e => setTry(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && tryName) useTry(); }} />
              <button type="button" className="btn btn-ink" disabled={!tryName || inUse} onClick={useTry}>Use this avatar</button>
            </div>
            <p className="hint">{!tryName ? 'Type any name to see the avatar it gives. Shape, colour, eyes, hair and clothes all come from the name.'
              : inUse ? 'This is your studio avatar now.'
                : 'This is how “' + tryName + '” looks in any app, from the name alone.' + (pinned ? ' Using it clears the ' + pinned + ' setting' + (pinned > 1 ? 's' : '') + ' you pinned in the studio.' : '')}</p>
          </div>
        </div>
        <div className="names">
          {NAMES.map(n => (
            <button key={n} type="button" className="name-card" aria-pressed={tryName === n} onClick={() => setTry(n)}>
              <div className="mini-stage"><Wobbli {...own(n)} controlled state="idle" size={76} /></div><span>{n}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="cta dk-panel" aria-labelledby="ctaTitle" data-theme="dark">
        <h2 id="ctaTitle">Give your agent a face</h2>
        <div className="row"><Link className="btn btn-light" to="/studio">Open the studio</Link><Link className="btn btn-ghost" to="/states">See all states</Link></div>
        <div className="cta-cards">
          <div className="cta-card"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 9l-4 3 4 3M16 9l4 3-4 3M13.5 6l-3 12" /></svg><div><code>npm install {PACKAGE_NAME}</code><small>Also pnpm, bun and the shadcn registry</small></div></div>
          <a className="cta-card" href={BLOBATAR_URL} target="_blank" rel="noopener"><svg viewBox="0 0 26 26" aria-hidden="true" style={{ stroke: 'none' }}><path d="M13 2.5c6.4 0 10.5 3.9 10.5 10.2 0 6.6-4.4 10.8-10.6 10.8C6.6 23.5 2.5 19.4 2.5 13 2.5 6.5 6.7 2.5 13 2.5z" fill="#FFFFFF" /><rect x="8.6" y="9.4" width="2.4" height="5.6" rx="1.2" fill="#111118" /><rect x="14.8" y="9" width="2.4" height="5.6" rx="1.2" fill="#111118" /></svg><div><b>Built on blobatar</b><small>Faces and shapes by Alain, MIT licence</small></div></a>
        </div>
      </section>
      <Footer />
    </div>
  );
}
